import API from "utils/API";
import Loan from "typedef/Loan";
import { usePlaidLink } from "react-plaid-link";
import { useCallback, useEffect, useState } from "react";
import useTracker from "components/CTracker/useTrackerContext";
import useUser from "context/UserCustomer/useUserCustomerContext";

const usePlaid = () => {
  const { user } = useUser();
  const { loan, type } = useTracker();
  const isBorrower = type === "borrower";
  const [warningIsOpen, setWarningIsOpen] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [otherEmploymentDialogIsOpen, setOtherEmploymentDialogIsOpen] =
    useState(false);
  const flags = isBorrower ? loan?.borrowerFlags : loan?.coborrowerFlags;
  const steps = isBorrower ? loan?.borrowerSteps : loan?.coborrowerSteps;
  const url = isBorrower ? "/set-borrower-flags" : "/set-coborrower-flags";

  /** usePlaidLink execute the modal of plaid and you get the plaidStatus */
  const plaid = usePlaidLink({
    token: linkToken,
    onEvent: async (eventName, eventMetadata) => {
      await API.post({
        url: `/save-plaid-event`,
        data: {
          loanId: loan?.id,
          event: {
            eventName,
            eventMetadata,
          },
        },
      });
    },
    onSuccess: async () => {
      if (user?.employmentType === "Self Employed") {
        await API.post({
          url: "/validate-income",
          data: {
            plaid_id: `${loan?.id}--${user.id}`,
            webhook_code: "BANK_INCOME_VERIFICATION",
          },
        });
      }

      if (!flags?.offeredAddingEmployment) {
        /**
         * To prevent the overlapping of plaid dialog
         * and other employment dialog breaking the scroll
         * we add a small timeout */
        setTimeout(() => setOtherEmploymentDialogIsOpen(true), 500);
      }
    },
  });

  const setFlags = useCallback(
    async (flags: Loan["borrowerFlags"] | Loan["coborrowerFlags"]) => {
      await API.post({
        url: url,
        data: flags,
      });
    },
    [url]
  );

  const loadEmployment = useCallback(async () => {
    setWarningIsOpen(false);
    const response = await API.get<{ link_token: string }>(
      `/get-plaid-income-link-token`
    );
    if ("error" in response) {
      console.error(response.error);
    } else {
      setLinkToken(response.data.link_token);
    }
  }, []);

  useEffect(() => {
    const isHomeValueVerificationStatusSuccess =
      loan?.borrowerSteps?.homeValuationVerification?.status === "success";

    const isCreditVerificationSuccess =
      loan?.coborrowerSteps?.creditVerification?.status === "success";

    if (isBorrower && !isHomeValueVerificationStatusSuccess) return;
    if (!isBorrower && !isCreditVerificationSuccess) return;

    if (
      !flags?.showedPlaidWarning &&
      steps?.incomeVerification?.status === "pending"
    ) {
      /** If the user was not warned yet, then show warning */
      setFlags({ showedPlaidWarning: true }).then(() => {
        setWarningIsOpen(true);
      });
    }

    if (flags?.showedPlaidWarning && !flags?.offeredAddingEmployment) {
      if (steps?.incomeVerification?.status === "success") {
        /** If the user was already warned, then request an employment load */
        setOtherEmploymentDialogIsOpen(true);
      } else if (
        steps?.incomeVerification?.status === "analyzing" ||
        steps?.incomeVerification?.status === "pending"
      ) {
        loadEmployment();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loan?.coborrowerSteps?.creditVerification?.status,
    loan?.borrowerSteps?.homeValuationVerification?.status,
  ]);

  useEffect(() => {
    if (plaid.ready) plaid.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plaid.ready, plaid.open]);

  return {
    setFlags,
    warningIsOpen,
    loadEmployment,
    otherEmploymentDialogIsOpen,
    setOtherEmploymentDialogIsOpen,
  };
};

export default usePlaid;
